import {
    BuildingOffice2Icon,
    ChevronRightIcon,
    CursorArrowRaysIcon,
    EnvelopeIcon,
    EnvelopeOpenIcon,
    PaperClipIcon,
    UserIcon,
} from "@heroicons/react/24/outline";
import { relativeTimeAgo, SentMessageId, SentMessageType, UserId } from "@sp-crm/core";
import { defaultLinkStyle } from "components/ui/link";
import {
    AdvancedSearchEntityType,
    ConversationFeedBasicsFragment,
    SentMessageFragmentFragment,
} from "generated/graphql";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCurrentUserId, useFeature, useUsers } from "store/selectors/hooks";
import { ReceivedMessage } from "./received-message-detail";
import { ReceivedMessageInlineDetail } from "./received-message-inline-detail";
import { SentMessageInlineDetail } from "./sent-message-inline-detail";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";

type EmailThreadType = NonNullable<
    ConversationFeedBasicsFragment["threads"][number]["emailThread"]
>;
type MailboxMessageType = EmailThreadType["mailboxMessages"][number];

interface ThreadMessage {
    id: string;
    type: "sent" | "received";
    timestamp: Date;
    senderName: string;
    bodyPreview: string;
    attachmentCount: number;
    openedCount: number;
    clickCount: number;
    originalSent?: SentMessageFragmentFragment;
    originalReceived?: MailboxMessageType;
}

interface UserLookup {
    [userId: string]: { name: string } | undefined;
}

function getSenderName(
    sent: SentMessageFragmentFragment,
    currentUserId: UserId | null,
    users: UserLookup,
): string {
    if (sent.userId === currentUserId) {
        return "You";
    }
    if (sent.userId && users[sent.userId]) {
        return users[sent.userId]?.name ?? "Unknown";
    }
    return "Unknown";
}

function getOpenedCount(sent: SentMessageFragmentFragment): number {
    return sent.events ? sent.events.filter(e => e.eventType === "opened").length : 0;
}

function getClickCount(sent: SentMessageFragmentFragment): number {
    return (
        sent.clickedLinkEvents?.reduce((acc, e) => acc + (e.eventsV2?.length || 0), 0) ||
        0
    );
}

function buildThreadMessages(
    emailThread: EmailThreadType,
    currentUserId: UserId | null,
    users: UserLookup,
): ThreadMessage[] {
    const messages: ThreadMessage[] = [];

    for (const sent of emailThread.sentMessages) {
        messages.push({
            id: sent.id,
            type: "sent",
            timestamp: new Date(sent.createdAt),
            senderName: getSenderName(sent, currentUserId, users),
            bodyPreview: sent.bodyPreview ?? sent.subject ?? "",
            attachmentCount: sent.attachments?.length ?? 0,
            openedCount: getOpenedCount(sent),
            clickCount: getClickCount(sent),
            originalSent: sent,
        });
    }

    for (const received of emailThread.mailboxMessages) {
        const senderName = received.sender?.name || received.from?.name || "Unknown";
        messages.push({
            id: received.id,
            type: "received",
            timestamp: new Date(received.receivedTimestamp),
            senderName,
            bodyPreview: received.bodyPreview,
            attachmentCount: 0,
            openedCount: 0,
            clickCount: 0,
            originalReceived: received,
        });
    }

    messages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    return messages;
}

function getThreadSubject(allMessages: ThreadMessage[]): string {
    if (allMessages.length === 0) {
        return "(no subject)";
    }
    const oldest = allMessages[allMessages.length - 1];
    if (oldest.type === "sent" && oldest.originalSent) {
        return oldest.originalSent.subject || "(no subject)";
    }
    if (oldest.type === "received" && oldest.originalReceived) {
        return oldest.originalReceived.subject || "(no subject)";
    }
    return "(no subject)";
}

interface SentMessageStatusProps {
    status: string;
    latestEventStatus: string | null | undefined;
    messageType: SentMessageType;
}

const SentMessageStatusIndicator: React.FC<SentMessageStatusProps> = props => {
    const { status, latestEventStatus, messageType } = props;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon" />;
    }

    return null;
};

function toReceivedMessage(mailboxMessage: MailboxMessageType): ReceivedMessage {
    return {
        id: mailboxMessage.id,
        receivedTimestamp: mailboxMessage.receivedTimestamp,
        subject: mailboxMessage.subject,
        bodyPreview: mailboxMessage.bodyPreview,
        from: mailboxMessage.from,
        to: mailboxMessage.to,
        cc: mailboxMessage.cc,
        bcc: mailboxMessage.bcc,
    };
}

interface EmailThreadComponentProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
    entityType?: AdvancedSearchEntityType;
}

const MAX_VISIBLE_MESSAGES = 3;

export const EmailThreadComponent: React.FC<EmailThreadComponentProps> = props => {
    const { thread, entityType } = props;
    const emailThread = thread.emailThread;
    const [expanded, setExpanded] = useState(false);
    const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(new Set());

    const currentUserId = useCurrentUserId();
    const usersState = useUsers();
    const enhancedMode = useFeature("enhancedSentMessageView");

    const allMessages = useMemo(() => {
        if (!emailThread) return [];
        return buildThreadMessages(emailThread, currentUserId, usersState.users);
    }, [emailThread, currentUserId, usersState.users]);

    const handleToggleExpand = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setExpanded(prev => !prev);
    }, []);

    const toggleMessageExpanded = useCallback((messageId: string) => {
        setExpandedMessageIds(prev => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    }, []);

    const intakeContext = useMemo(() => {
        const sentMessages = emailThread?.sentMessages ?? [];
        for (const sent of sentMessages) {
            if (sent.intakeClient || sent.intakeCommunity) {
                return {
                    client: sent.intakeClient,
                    community: sent.intakeCommunity,
                };
            }
        }
        return null;
    }, [emailThread?.sentMessages]);

    const stopPropagation = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
    }, []);

    if (!emailThread) {
        return null;
    }

    const totalCount = allMessages.length;
    const hiddenCount = totalCount - MAX_VISIBLE_MESSAGES;
    const visibleMessages = expanded
        ? allMessages
        : allMessages.slice(0, MAX_VISIBLE_MESSAGES);

    const subject = getThreadSubject(allMessages);

    const client =
        entityType === AdvancedSearchEntityType.Community ? intakeContext?.client : null;
    const community =
        entityType === AdvancedSearchEntityType.Client ? intakeContext?.community : null;

    return (
        <li key={thread.id}>
            <div className="border border-gray-200 rounded-sm overflow-hidden">
                <div className="flex items-center gap-3 px-3 py-2 bg-gray-50">
                    <EnvelopeIcon
                        className="h-4 w-4 text-gray-400 flex-shrink-0"
                        aria-hidden="true"
                    />
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 truncate">
                            {subject}
                        </span>
                        {community && (
                            <Link
                                to={community.appLink}
                                onClick={stopPropagation}
                                className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800 hover:underline flex-shrink-0">
                                <BuildingOffice2Icon
                                    className="h-3.5 w-3.5"
                                    aria-hidden="true"
                                />
                                {community.name ?? "(no name)"}
                            </Link>
                        )}
                        {client && (
                            <Link
                                to={client.appLink}
                                onClick={stopPropagation}
                                className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800 hover:underline flex-shrink-0">
                                <UserIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                {client.name ?? client.bestContactName ?? "(no name)"}
                            </Link>
                        )}
                    </div>
                </div>
                <div>
                    {visibleMessages.map(message => {
                        const isExpanded = expandedMessageIds.has(message.id);

                        if (isExpanded) {
                            if (message.type === "sent" && message.originalSent) {
                                return (
                                    <SentMessageInlineDetail
                                        key={message.id}
                                        messageId={
                                            message.originalSent.id as SentMessageId
                                        }
                                        attachmentCount={message.attachmentCount}
                                        status={message.originalSent.status}
                                        latestEventStatus={
                                            message.originalSent.latestEventStatus
                                        }
                                        messageType={
                                            message.originalSent
                                                .messageType as SentMessageType
                                        }
                                        onCollapse={() =>
                                            toggleMessageExpanded(message.id)
                                        }
                                        openedCount={message.openedCount}
                                        clickCount={message.clickCount}
                                        senderName={message.senderName}
                                    />
                                );
                            }
                            if (message.type === "received" && message.originalReceived) {
                                return (
                                    <ReceivedMessageInlineDetail
                                        key={message.id}
                                        message={toReceivedMessage(
                                            message.originalReceived,
                                        )}
                                        onCollapse={() =>
                                            toggleMessageExpanded(message.id)
                                        }
                                    />
                                );
                            }
                        }

                        return (
                            <div
                                key={message.id}
                                className="flex items-center gap-3 px-3 py-2 pl-8 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                                onClick={() => toggleMessageExpanded(message.id)}>
                                <ChevronRightIcon
                                    className="h-3 w-3 text-gray-400 flex-shrink-0 transition-transform duration-300"
                                    aria-hidden="true"
                                />
                                <div className="flex-1 min-w-0 flex items-center gap-2">
                                    <span className="font-medium text-sm text-gray-900 truncate max-w-[120px]">
                                        {message.senderName}
                                    </span>
                                    <span className="text-sm text-gray-500 truncate flex-1">
                                        {message.bodyPreview}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                                    {message.type === "sent" && (
                                        <>
                                            {message.attachmentCount > 0 && (
                                                <span className="inline-flex items-center gap-0.5">
                                                    <PaperClipIcon className="h-3.5 w-3.5 text-gray-400" />
                                                    {message.attachmentCount}
                                                </span>
                                            )}
                                            {enhancedMode && (
                                                <>
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <EnvelopeOpenIcon className="h-3.5 w-3.5 text-gray-400" />
                                                        {message.openedCount}
                                                    </span>
                                                    <span className="inline-flex items-center gap-0.5">
                                                        <CursorArrowRaysIcon className="h-3.5 w-3.5 text-gray-400" />
                                                        {message.clickCount}
                                                    </span>
                                                </>
                                            )}
                                        </>
                                    )}
                                    {message.type === "sent" && message.originalSent && (
                                        <SentMessageStatusIndicator
                                            messageType={
                                                message.originalSent
                                                    .messageType as SentMessageType
                                            }
                                            status={message.originalSent.status}
                                            latestEventStatus={
                                                message.originalSent.latestEventStatus
                                            }
                                        />
                                    )}
                                    <span className="min-w-[75px] text-right">
                                        {relativeTimeAgo(message.timestamp)}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                    {hiddenCount > 0 && (
                        <div
                            className={`px-3 py-2 pl-8 text-sm cursor-pointer border-b border-gray-100 ${defaultLinkStyle}`}
                            onClick={handleToggleExpand}>
                            {expanded
                                ? "Show fewer messages"
                                : `+${hiddenCount} older ${hiddenCount === 1 ? "message" : "messages"}`}
                        </div>
                    )}
                </div>
            </div>
        </li>
    );
};
